import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Eye, EyeOff, Globe } from 'lucide-react';
import { getAllPageContent, updatePageContent, PageContent, getPageMeta, togglePagePublish, PageMeta } from '../utils/storage';

const ContentEditor: React.FC = () => {
  const navigate = useNavigate();
  const { pageSlug } = useParams<{ pageSlug: string }>();
  const [content, setContent] = useState<PageContent[]>([]);
  const [pageMeta, setPageMeta] = useState<PageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [togglingPublish, setTogglingPublish] = useState(false);

  useEffect(() => {
    const loadContent = async () => {
      if (pageSlug) {
        const [pageContent, meta] = await Promise.all([
          getAllPageContent(pageSlug),
          getPageMeta(pageSlug)
        ]);
        setContent(pageContent);
        setPageMeta(meta);
      }
      setLoading(false);
    };
    loadContent();
  }, [pageSlug]);

  const handleTogglePublish = async () => {
    if (!pageSlug || !pageMeta) return;

    setTogglingPublish(true);
    const newStatus = !pageMeta.is_published;
    const success = await togglePagePublish(pageSlug, newStatus);

    if (success) {
      setPageMeta({ ...pageMeta, is_published: newStatus });
    } else {
      alert('Error al cambiar el estado de publicación.');
    }
    setTogglingPublish(false);
  };

  const handleContentChange = (id: string, newContent: string) => {
    setContent(prev => prev.map(item =>
      item.id === id ? { ...item, content: newContent } : item
    ));
  };

  const handleSave = async (item: PageContent) => {
    setSaving(item.id);
    const success = await updatePageContent(item.id, item.content);
    setSaving(null);

    if (success) {
      setSavedMessage(item.section_key);
      setTimeout(() => setSavedMessage(null), 2000);
    } else {
      alert('Error al guardar. Por favor, intenta de nuevo.');
    }
  };

  const handleSaveAll = async () => {
    setSaving('all');
    for (const item of content) {
      await updatePageContent(item.id, item.content);
    }
    setSaving(null);
    setSavedMessage('all');
    setTimeout(() => setSavedMessage(null), 2000);
  };

  const formatSectionKey = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const getContentTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'text': 'Texto',
      'html': 'HTML',
      'json': 'JSON (Lista)',
      'image_url': 'URL de Imagen'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div>
      <section className="bg-gradient-to-r from-red-600 to-red-800 text-white py-16">
        <div className="container mx-auto px-4">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center text-white hover:underline mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al panel
          </button>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-4 mb-2">
                <h1 className="text-4xl font-bold">
                  Editar Página: {pageSlug?.toUpperCase()}
                </h1>
                {pageMeta && (
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    pageMeta.is_published
                      ? 'bg-green-500 text-white'
                      : 'bg-yellow-500 text-yellow-900'
                  }`}>
                    {pageMeta.is_published ? 'Publicada' : 'Borrador'}
                  </span>
                )}
              </div>
              <p className="text-red-200">
                {content.length} secciones editables
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleTogglePublish}
                disabled={togglingPublish}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition font-semibold disabled:opacity-50 ${
                  pageMeta?.is_published
                    ? 'bg-yellow-500 hover:bg-yellow-400 text-yellow-900'
                    : 'bg-green-500 hover:bg-green-400 text-white'
                }`}
              >
                {pageMeta?.is_published ? (
                  <>
                    <EyeOff className="w-5 h-5" />
                    {togglingPublish ? 'Ocultando...' : 'Ocultar Página'}
                  </>
                ) : (
                  <>
                    <Globe className="w-5 h-5" />
                    {togglingPublish ? 'Publicando...' : 'Publicar Página'}
                  </>
                )}
              </button>
              <a
                href={`/${pageSlug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg flex items-center gap-2 transition"
              >
                <Eye className="w-5 h-5" />
                Vista Previa
              </a>
              <button
                onClick={handleSaveAll}
                disabled={saving === 'all'}
                className="bg-white text-red-600 hover:bg-red-50 px-6 py-2 rounded-lg font-semibold flex items-center gap-2 transition disabled:opacity-50"
              >
                <Save className="w-5 h-5" />
                {saving === 'all' ? 'Guardando...' : 'Guardar Todo'}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="container mx-auto px-4">
          {savedMessage === 'all' && (
            <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
              Todos los cambios guardados correctamente
            </div>
          )}

          <div className="space-y-6">
            {content.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-800">
                      {formatSectionKey(item.section_key)}
                    </h3>
                    <span className="text-sm text-gray-500">
                      Tipo: {getContentTypeLabel(item.content_type)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {savedMessage === item.section_key && (
                      <span className="text-green-600 text-sm">Guardado</span>
                    )}
                    <button
                      onClick={() => handleSave(item)}
                      disabled={saving === item.id}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      {saving === item.id ? 'Guardando...' : 'Guardar'}
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  {item.content_type === 'json' ? (
                    <textarea
                      value={item.content}
                      onChange={(e) => handleContentChange(item.id, e.target.value)}
                      rows={10}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      placeholder="JSON content..."
                    />
                  ) : item.content_type === 'image_url' ? (
                    <div className="space-y-4">
                      <input
                        type="text"
                        value={item.content}
                        onChange={(e) => handleContentChange(item.id, e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                        placeholder="https://..."
                      />
                      {item.content && (
                        <div className="relative h-48 rounded-lg overflow-hidden bg-gray-100">
                          <img
                            src={item.content}
                            alt="Preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <textarea
                      value={item.content}
                      onChange={(e) => handleContentChange(item.id, e.target.value)}
                      rows={item.content.length > 100 ? 4 : 2}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      placeholder="Contenido..."
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContentEditor;
